import React, { useEffect, useState, Suspense, useMemo, useRef } from 'react';
import './StorePanelStation.css';
import Layout from '../../../Components/Layout/Layout';
import { useBusinessLogin } from '../../../hooks/auth/useBusinessLogin';
import { usePost } from '../../../hooks/usePost';
import { useBEARER } from '../../../hooks/auth/useBEARER';
import { collebration_permission } from '../../../hooks/auth/useCollebrationPermission';
import { IoLockClosed, IoWarningOutline, IoConstructOutline } from "react-icons/io5";
import StoreHeader from '../../../Components/StoreHeader/StoreHeader';
import api from '../../../hooks/api';
import TelegramSafeArea from '../../../Components/TelegramSafeArea';
import { useLocation } from 'react-router-dom';
import config from '../../../config';
import StorePanelBuy from '../StorePanelBuy/StorePanelBuy';

// --- Lazy Loads ---
const StoreForm = React.lazy(() => import('../StoreForm/StoreForm'));
const StoreInvoice = React.lazy(() => import('../StoreInvoice/StoreInvoice'));
const StoreordersCancel = React.lazy(() => import('../StoreordersCancel/StoreordersCancel'));
const StoreEmployee = React.lazy(() => import('../StoreEmployee/StoreEmployee'));
const ProductsMenu = React.lazy(() => import('../StoreProducts/ProductsMenu'));
const BulkSMSPanel = React.lazy(() => import('../BulkSMSPanel/BulkSMSPanel'));
const StoreDashboard = React.lazy(() => import('../StoreDashboard/StoreDashboard'));

const LoadingSpinner = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', color: '#666' }}>
        در حال بارگذاری...
    </div>
);

// 🔹 استخراج ایمن پارامتر استارت تلگرام و بررسی مصرف‌شدن قبلی
const extractStartParam = (locationState) => {
    let param = null;

    if (locationState?.startParam) {
        param = String(locationState.startParam).trim().toLowerCase();
    } else if (window.Telegram?.WebApp?.initDataUnsafe?.start_param) {
        param = String(window.Telegram.WebApp.initDataUnsafe.start_param).trim().toLowerCase();
    } else {
        try {
            const searchParams = new URLSearchParams(window.location.search);
            const searchParam = searchParams.get('tgWebAppStartParam') || searchParams.get('startapp') || searchParams.get('start_param');
            if (searchParam) param = String(searchParam).trim().toLowerCase();
        } catch (e) { }

        if (!param) {
            try {
                if (window.location.hash) {
                    const rawHash = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : window.location.hash;
                    const hashParams = new URLSearchParams(rawHash);
                    const hashParam = hashParams.get('tgWebAppStartParam') || hashParams.get('start_param') || hashParams.get('startapp');
                    if (hashParam) {
                        param = String(hashParam).trim().toLowerCase();
                    } else {
                        const webAppData = hashParams.get('tgWebAppData');
                        if (webAppData) {
                            const innerParams = new URLSearchParams(webAppData);
                            const innerStartParam = innerParams.get('start_param');
                            if (innerStartParam) param = String(innerStartParam).trim().toLowerCase();
                        }
                    }
                }
            } catch (e) { }
        }
    }

    if (!param) return null;

    // اگر قبلاً در همین نشست تلگرام مصرف شده، نادیده بگیر
    const consumedParam = sessionStorage.getItem('consumed_telegram_start_param');
    if (consumedParam === param) {
        return null;
    }

    return param;
};

// 🔹 تعیین منوی مقصد براساس اکشن‌های تلگرام
const getMenuIdByAction = (param) => {
    if (!param) return null;
    const cleanParam = String(param).trim().toLowerCase();
    const actions = config.telegram_actions || {};

    switch (cleanParam) {
        case 'seller_new_order':
        case actions.SELLER_NEW_ORDER?.toLowerCase():
            return 5; // سفارشات جدید (با توجه به اضافه شدن منوی ۲، شماره ۵ است)

        case 'buyer_accept_collaboration':
        case 'buyer_cancel_collaboration':
        case actions.BUYER_ACCEPT_COLLABORATION?.toLowerCase():
        case actions.BUYER_CANCEL_COLLABORATION?.toLowerCase():
            return 11; // همکاران (با توجه به شیفت منوها، شماره ۱۱ است)

        default:
            return null;
    }
};

function StorePanelStation() {
    const location = useLocation();
    const startParam = useMemo(() => extractStartParam(location.state), [location.state]);

    const [menuId, setMenuId] = useState(1);
    const [collebrationId, setCollebrationId] = useState(0);
    const [collebration, setCollebration] = useState(null);

    const [credits, setCredits] = useState(null);
    const [isLoadingCredits, setIsLoadingCredits] = useState(true);

    const [localBusinessData, setLocalBusinessData] = useState(null);

    const effectRan = useRef(false);
    const hasHandledInitialRoute = useRef(false);

    const { userId } = useBEARER();
    const { data: Collebrations_data } = usePost(userId ? `collebration/list/user/active` : null, { user_id: userId, rowCount: 5 });

    // 🔹 دریافت همه‌جانبه مشخصات بیزینس، وضعیت اشتراک و چت‌آیدی از useBusinessLogin
    const {
        data: business_data,
        login: isBusinessLogin,
        message: businessMessage,
        isloading: isBusinessLoading,
        startDate,
        endDate,
        days,
        is_credit_valid,
        ChatId
    } = useBusinessLogin();

    const collebrations = Collebrations_data?.data?.data || [];

    useEffect(() => {
        if (business_data) {
            setLocalBusinessData(business_data);
        }
    }, [business_data]);

    useEffect(() => {
        const handleBusinessUpdate = (e) => {
            const updatedData = e.detail;
            if (!updatedData) return;
            setLocalBusinessData(prev => ({
                ...(prev || {}),
                ...updatedData,
                businessStatus_id: updatedData?.businessStatus_id || 19
            }));
        };

        window.addEventListener('businessInfoUpdated', handleBusinessUpdate);
        return () => {
            window.removeEventListener('businessInfoUpdated', handleBusinessUpdate);
        };
    }, []);

    const effectiveBusinessData = localBusinessData || business_data;
    const currentBusiness = collebration || effectiveBusinessData;

    const hasBusiness = Boolean(effectiveBusinessData && isBusinessLogin);
    const businessStatusId = Number(currentBusiness?.businessStatus_id || effectiveBusinessData?.businessStatus_id || 0);

    const isUnderConstruction = hasBusiness && businessStatusId === 18;
    const isStoreInactive = !hasBusiness || businessStatusId === 20;
    const isStoreActive = hasBusiness && businessStatusId === 19;

    // 🔹 وضعیت فعال بودن اشتراک با بررسی فیلد سرور یا روزهای باقی‌مانده
    const hasActiveSubscription = Boolean(hasBusiness && (is_credit_valid ?? (days > 0)));

    const permissions = useMemo(() => {
        const newPermissions = [];
        if (Array.isArray(collebrations)) {
            collebrations.forEach((item) => {
                if (item.permission) {
                    try {
                        const parsedPermission = (typeof item.permission === 'string')
                            ? JSON.parse(item.permission)
                            : item.permission;

                        if (Array.isArray(parsedPermission)) {
                            parsedPermission.forEach((i) => {
                                newPermissions.push({
                                    collebration_id: item.id,
                                    permission_id: i.id,
                                    title: i.title,
                                    checked: i.checked
                                });
                            });
                        }
                    } catch (e) {
                        console.error("خطا در پردازش دسترسی‌ها:", e);
                    }
                }
            });
        }
        return newPermissions;
    }, [Collebrations_data]);

    // دریافت پلن‌های اعتبار
    useEffect(() => {
        if (effectRan.current === true) return;
        setIsLoadingCredits(true);
        api.get(`credit/all`)
            .then(response => {
                if (response.data?.success) {
                    setCredits(response.data.data);
                } else {
                    setCredits(response.data);
                }
            })
            .catch(err => {
                console.error("Error fetching credits:", err);
                setCredits(null);
            })
            .finally(() => setIsLoadingCredits(false));

        return () => {
            effectRan.current = true;
        };
    }, []);

    const menuItems = [
        { id: 1, title: "خرید و تمدید اشتراک", icon: "1.png", permIndex: 0 },
        { id: 2, title: "داشبورد تحلیلی", icon: "1.png", permIndex: 0 },
        { id: 3, title: "پیکربندی", icon: "2.png", permIndex: 1 },
        { id: 4, title: "تعریف کالا", icon: "3.png", permIndex: 2 },
        { id: 5, title: "سفارشات جدید", icon: "4.png", permIndex: 3 },
        { id: 6, title: "آماده سازی و بسته بندی", icon: "5.png", permIndex: 4 },
        { id: 7, title: "ارسال به پست", icon: "6.png", permIndex: 5 },
        { id: 8, title: "تحویل به پست", icon: "7.png", permIndex: 6 },
        { id: 9, title: "سفارشات تکمیل شده", icon: "8.png", permIndex: 7 },
        { id: 10, title: "فاکتورهای ابطال شده", icon: "9.png", permIndex: 8 },
        { id: 11, title: "همکار", icon: "10.png", permIndex: 0 },
        { id: 12, title: "اعلان مشتریان", icon: "11.png", permIndex: 9 },
    ];

    const checkIsMenuDisabled = (item) => {
        if (!hasBusiness || isStoreInactive) {
            return item.id !== 1;
        }

        if (isUnderConstruction) {
            return item.id !== 1 && item.id !== 2;
        }

        const isSubscriptionDisabled = !hasActiveSubscription && item.id !== 1;

        const permissionResult = collebrationId !== 0
            ? collebration_permission(permissions, collebrationId, item.permIndex)
            : { allow: true };

        return isSubscriptionDisabled || !permissionResult.allow;
    };

    // 🔹 مسیردهی اولیه فقط پس از اتمام بارگذاری بیزینس و اعتبارات
    useEffect(() => {
        if (isBusinessLoading || isLoadingCredits) return;
        if (hasHandledInitialRoute.current) return;

        if (!hasBusiness || isStoreInactive) {
            setMenuId(1);
        } else if (isUnderConstruction) {
            setMenuId(2);
        } else if (!hasActiveSubscription) {
            setMenuId(1);
        } else {
            const targetMenuId = getMenuIdByAction(startParam);
            const targetItem = targetMenuId ? menuItems.find(m => m.id === targetMenuId) : null;

            if (targetItem && !checkIsMenuDisabled(targetItem)) {
                setMenuId(targetMenuId);

                // 🔥 ثبت پارامتر به عنوان مصرف‌شده جهت جلوگیری از اجرای مجدد در جابجایی بین صفحات
                if (startParam) {
                    sessionStorage.setItem('consumed_telegram_start_param', startParam);

                    // پاکسازی از آبجکت گلوبال تلگرام
                    if (window.Telegram?.WebApp?.initDataUnsafe?.start_param) {
                        window.Telegram.WebApp.initDataUnsafe.start_param = null;
                    }

                    // پاکسازی پارامتر از URL بدون رفرش شدن صفحه
                    try {
                        const url = new URL(window.location.href);
                        url.searchParams.delete('tgWebAppStartParam');
                        url.searchParams.delete('startapp');
                        url.searchParams.delete('start_param');
                        window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
                    } catch (e) { }
                }
            } else {
                setMenuId(1);
            }
        }

        hasHandledInitialRoute.current = true;
    }, [
        isBusinessLoading,
        isLoadingCredits,
        hasBusiness,
        isStoreInactive,
        isUnderConstruction,
        hasActiveSubscription,
        startParam,
        collebrationId,
        permissions
    ]);


    const handleSelectCollaboration = (selectedCollab) => {
        if (selectedCollab) {
            setCollebrationId(selectedCollab.id);
            setCollebration(selectedCollab);
            setMenuId(1);
        } else {
            setCollebrationId(0);
            setCollebration(null);
            setMenuId(1);
        }
    };

    const handleMenuClick = (item) => {
        if (checkIsMenuDisabled(item)) return;
        setMenuId(item.id);
    };

    const isPageLoading = isBusinessLoading || isLoadingCredits;

    const getStoreStatusLabel = (isCollab = false) => {
        const prefix = isCollab ? "(همکار)" : "(فروشگاه من)";
        if (!hasBusiness || isStoreInactive) return `${prefix} 🛑 متوقف`;
        if (isUnderConstruction) return `${prefix} 🛠 در حال ساخت`;
        return prefix;
    };

    const formattedDefaultBusiness = (effectiveBusinessData && !Array.isArray(effectiveBusinessData) && effectiveBusinessData.id) ? {
        ...effectiveBusinessData,
        storeLabel: getStoreStatusLabel(false)
    } : null; // اگر بیزینسی وجود ندارد null ارسال شود


    const formattedCollabs = collebrations.map(c => ({
        ...c,
        storeLabel: getStoreStatusLabel(true)
    }));

    return (
        <Layout hideHeader={true}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>

                <TelegramSafeArea style={{ background: "#fff", color: "#000" }}>
                    <div
                        className="safe-area-title"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '1px',
                            padding: '2px 0 4px 0'
                        }}
                    >
                        <img
                            src="/images/sabad1_telegram_safe_optimized.png"
                            alt="سبد 1"
                            style={{
                                width: '32px',
                                height: '32px',
                                objectFit: 'contain',
                                display: 'block'
                            }}
                        />
                        <span
                            style={{
                                fontSize: '13px',
                                fontWeight: 'bold',
                                color: '#000000',
                                fontFamily: 'vazir, sans-serif',
                                lineHeight: '1.1'
                            }}
                        >
                            سبد 1
                        </span>
                    </div>
                </TelegramSafeArea>

                <StoreHeader
                    title="پنل فروشگاه"
                    isLoading={isBusinessLoading} // 👈 حتماً وضعیت لودینگ را ارسال کنید
                    activeCollab={collebration}
                    defaultBusiness={formattedDefaultBusiness}
                    allCollabs={formattedCollabs}
                    onSelectCollab={handleSelectCollaboration}
                    startParam={startParam}
                />

                <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                    <div className="store-panel-station-wrraper" style={{ height: '100%' }}>
                        <div className='store-panel-station'>

                            {/* سایدبار منوها */}
                            <div className={`panel-right`}>
                                {menuItems.map((item) => {
                                    const isDisabled = checkIsMenuDisabled(item);

                                    return (
                                        <div
                                            key={item.id}
                                            className={`categore${menuId === item.id ? " active" : ""} ${isDisabled ? " disabled" : ""}`}
                                            onClick={() => handleMenuClick(item)}
                                            style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed', position: 'relative' } : {}}
                                        >
                                            <img src={`./images/adminIcons/${item.icon}`} alt="Icon" />
                                            <span value={item.id}>{item.title}</span>
                                            {isDisabled && (
                                                <div style={{ position: 'absolute', left: '10px', top: '30px', color: '#666' }}>
                                                    <IoLockClosed />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* بدنه محتوا */}
                            <div className="panel-left">
                                <div className="other-managers"></div>

                                <div className="panel-left-wrapper">

                                    {/* هشدار عدم وجود یا توقف فروشگاه */}
                                    {!isPageLoading && (!hasBusiness || isStoreInactive) && menuId === 1 && (
                                        <div style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                            padding: '15px', textAlign: 'center',
                                            background: '#fff5f5', border: '2px solid #e74c3c', borderRadius: '12px', margin: '15px 15px 5px 15px'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                <IoWarningOutline size={26} color="#e74c3c" />
                                                <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#c0392b' }}>
                                                    {!hasBusiness ? 'اطلاعات فروشگاه یافت نشد' : 'فروشگاه در وضعیت متوقف'}
                                                </span>
                                            </div>
                                            <p style={{ fontSize: '13px', lineHeight: '1.8', color: '#c0392b', margin: 0, whiteSpace: 'pre-line' }}>
                                                {businessMessage || (!hasBusiness ? 'برای شروع و راه‌اندازی فروشگاه ابتدا اشتراک تهیه نمایید.' : 'فروشگاه در حالت متوقف یا غیرفعال قرار دارد.')}
                                            </p>
                                        </div>
                                    )}

                                    {/* هشدار در حال ساخت */}
                                    {!isPageLoading && isUnderConstruction && businessMessage && menuId === 1 && (
                                        <div style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                            padding: '12px 15px', textAlign: 'center',
                                            background: '#fffbf0', border: '1.5px solid #f39c12', borderRadius: '12px', margin: '15px 15px 5px 15px'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                <IoConstructOutline size={24} color="#e67e22" />
                                                <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#d35400' }}>فروشگاه در حال راه‌اندازی</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* هشدار اتمام اشتراک */}
                                    {!isPageLoading && isStoreActive && !hasActiveSubscription && menuId === 1 && (
                                        <div style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                            padding: '12px 15px', textAlign: 'center',
                                            background: '#fff5f5', border: '1.5px solid #e74c3c', borderRadius: '12px', margin: '15px 15px 5px 15px'
                                        }}>
                                            <p style={{ fontSize: '13px', lineHeight: '1.8', color: '#c0392b', margin: 0, whiteSpace: 'pre-line' }}>
                                                اعتبار اشتراک شما به پایان رسیده است. جهت دسترسی به امکانات، اشتراک خود را تمدید فرمایید.
                                            </p>
                                        </div>
                                    )}

                                    <Suspense fallback={<LoadingSpinner />}>
                                        {menuId === 1 && (
                                            <StorePanelBuy
                                                isToolbarOpen={false}
                                                isLoading={isPageLoading}
                                                availableCredits={credits}
                                                subscriptionDays={days}
                                                subscriptionEndDate={endDate}
                                                chatId={ChatId}
                                            />
                                        )}
                                        {/* رندر شدن کامپوننت داشبورد برای منوی شماره ۲ */}
                                        {menuId === 2 && <StoreDashboard CollebrationId={collebrationId} />}
                                        {menuId === 3 && <StoreForm is_toolbar_hidden={false} />}
                                        {menuId === 4 && <ProductsMenu is_toolbar_hidden={false} CollebrationId={collebrationId} />}
                                        {menuId === 5 && <StoreInvoice CollebrationId={collebrationId} pageName={"order"} invoiceStatusId={2} nextStatusId={3} isToolbarOpen={false} />}
                                        {menuId === 6 && <StoreInvoice CollebrationId={collebrationId} pageName={"ordering"} invoiceStatusId={3} nextStatusId={4} isToolbarOpen={false} />}
                                        {menuId === 7 && <StoreInvoice CollebrationId={collebrationId} pageName={"orderPostSend"} invoiceStatusId={4} nextStatusId={5} isToolbarOpen={false} />}
                                        {menuId === 8 && <StoreInvoice CollebrationId={collebrationId} pageName={"orderPostDelivery"} invoiceStatusId={5} nextStatusId={6} isToolbarOpen={false} />}
                                        {menuId === 9 && <StoreInvoice CollebrationId={collebrationId} pageName={"ordersComplete"} invoiceStatusId={6} nextStatusId={6} isToolbarOpen={false} />}
                                        {menuId === 10 && (
                                            <StoreInvoice
                                                CollebrationId={collebrationId}
                                                pageName={"ordersRepealed"}
                                                invoiceStatusId={7}
                                                nextStatusId={null}
                                                isToolbarOpen={false}
                                            />
                                        )}
                                        {menuId === 11 && <StoreEmployee />}
                                        {menuId === 12 && <BulkSMSPanel collebrationId={collebrationId} />}
                                    </Suspense>

                                    <div style={{ height: 'calc(env(safe-area-inset-bottom, 20px) )', width: '100%', flexShrink: 0 }} />
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default StorePanelStation;
